import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { StaffSpeciality } from '@/types/settings-api';

/**
 * Procedure-code master search — PROD-761 commit 6 (Skills typeahead).
 *
 * GET /api/Master/ProcedureCodesList?searchQuery={q}
 *
 * Source for the Professional Details Skills section. Each entry is
 * shaped exactly like `StaffSpeciality` — same `{specialitySkillId,
 * code: {code, displayName, system, description?}}` envelope used by
 * `ProfessionalDetails.skills`, so consumers can append the master
 * item directly to the draft array (just zero out the per-row `id`).
 *
 * Response shape verified against live dev API 2026-05-14:
 *   [{
 *     specialitySkillId: "1bbdb9a1-...",
 *     code: {
 *       code: "00873",
 *       displayName: "ANESTH KIDNEY STONE DESTRUCT",
 *       system: "CPT",
 *       description: "Anesthesia for lithotripsy, ..."
 *     }
 *   }, ...]
 *
 * Caller debounces its `searchQuery` input (same pattern as
 * `useLanguageSearch`). Empty query short-circuits to avoid hitting
 * the API on mount — the procedure-codes catalogue is large (thousands
 * of CPT codes), so we never want to return everything at once.
 */
export function useProcedureCodesSearch(searchQuery: string) {
  const trimmed = searchQuery.trim();
  return useQuery({
    queryKey: ['settings', 'master', 'procedure-codes', trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed });
      return get<StaffSpeciality[]>(
        `/api/Master/ProcedureCodesList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: trimmed.length > 0,
    staleTime: 60_000,
  });
}
